import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { downloadBlob } from "@/shared/utils";

interface AuditPdfRow {
  cells: string[];
}

interface AuditPdfOptions {
  title: string;
  subtitle: string;
  columns: string[];
  rows: AuditPdfRow[];
  filename: string;
  labels: {
    generatedAt: string;
  };
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

async function registerCyrillicFonts(doc: jsPDF): Promise<void> {
  const [regularBuf, boldBuf] = await Promise.all([
    fetch("/fonts/Roboto-Regular.ttf").then((r) => r.arrayBuffer()),
    fetch("/fonts/Roboto-Bold.ttf").then((r) => r.arrayBuffer()),
  ]);

  doc.addFileToVFS("Roboto-Regular.ttf", arrayBufferToBase64(regularBuf));
  doc.addFont("Roboto-Regular.ttf", "Roboto", "normal");

  doc.addFileToVFS("Roboto-Bold.ttf", arrayBufferToBase64(boldBuf));
  doc.addFont("Roboto-Bold.ttf", "Roboto", "bold");

  doc.setFont("Roboto");
}

export type { AuditPdfOptions, AuditPdfRow };

export async function generateAuditPdf(options: AuditPdfOptions): Promise<void> {
  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });

  await registerCyrillicFonts(doc);

  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 14;
  let y = margin;

  doc.setFont("Roboto", "bold");
  doc.setFontSize(18);
  doc.text(options.title, margin, y);

  y += 8;

  doc.setFont("Roboto", "normal");
  doc.setFontSize(12);
  doc.setTextColor(100);
  doc.text(options.subtitle, margin, y);

  y += 10;

  doc.setTextColor(0);
  doc.setFont("Roboto", "normal");

  const bodyData = options.rows.map((row) => row.cells);

  autoTable(doc, {
    startY: y,
    head: [options.columns],
    body: bodyData,
    margin: { left: margin, right: margin },
    styles: {
      fontSize: 8,
      cellPadding: 3,
      font: "Roboto",
      overflow: "linebreak",
    },
    headStyles: {
      fillColor: [41, 128, 186],
      textColor: 255,
      fontStyle: "bold",
      font: "Roboto",
    },
    alternateRowStyles: {
      fillColor: [245, 245, 245],
    },
    columnStyles: {
      0: { cellWidth: 35 },
      1: { cellWidth: 40 },
      2: { cellWidth: 35 },
      3: { cellWidth: 50 },
      4: { cellWidth: "auto" },
    },
  });

  const totalPages = doc.getNumberOfPages();
  const dateStr = new Date().toLocaleDateString();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFont("Roboto", "normal");
    doc.setFontSize(8);
    doc.setTextColor(150);
    const pageHeight = doc.internal.pageSize.getHeight();
    doc.text(`${options.labels.generatedAt}: ${dateStr}`, margin, pageHeight - 8);
    doc.text(`${i} / ${totalPages}`, pageWidth - margin - doc.getTextWidth(`${i} / ${totalPages}`), pageHeight - 8);
  }

  const blob = doc.output("blob");
  downloadBlob(blob, options.filename);
}
